import { Button } from "@/components/generic/Button";
import { NewCampaign, Persona, type Configuration } from "@/types/campaign";
import { Reorder } from "framer-motion";
import { Plus } from "lucide-react";
import { useState } from "react";
import { AddPersonaModal } from "./AddPersonaModal";
import EmptyState from "./EmptyState";
import { PersonaListItem } from "./PersonaListItem";

interface PersonaFormProps {
  configuration: Configuration;
  personas: Persona[];
  onAddPersona: (persona: Persona) => void;
  onRemovePersona: (index: number) => void;
  onReorderPersona: (personas: Persona[]) => void;
  onUpdatePersona?: (index: number, persona: Persona) => void;
  editingPersonaIndex: number | null;
  setEditingPersonaIndex: (index: number | null) => void;
}

export function PersonaForm({
  configuration,
  personas,
  onAddPersona,
  onRemovePersona,
  onReorderPersona,
  onUpdatePersona,
  editingPersonaIndex,
  setEditingPersonaIndex,
}: PersonaFormProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = (index: number | null = null) => {
    setEditingPersonaIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = (open: boolean) => {
    if (!open) {
      setIsModalOpen(false);
      setEditingPersonaIndex(null);
    }
  };

  const handleSave = (persona: Persona) => {
    const indexToUpdate = editingPersonaIndex;

    setIsModalOpen(false);
    setEditingPersonaIndex(null);

    if (indexToUpdate !== null && onUpdatePersona) {
      onUpdatePersona(indexToUpdate, persona);
    } else {
      onAddPersona(persona);
    }
  };

  const editingPersona =
    editingPersonaIndex !== null ? personas[editingPersonaIndex] : undefined;

  const showEmptyState = personas.length === 0;

  return (
    <div className="w-full space-y-4">
      {!showEmptyState ? (
        <div className="flex w-full justify-center">
          <div className="flex max-w-158 min-w-158 flex-col gap-6">
            <div className="flex items-center justify-between">
              <div className="ml-8 flex items-center gap-3">
                <span className="text-sm font-semibold">Valgte personer</span>
              </div>
              <Button
                intent="secondary"
                animate
                className="h-11"
                onPress={() => handleOpenModal()}
              >
                <div className="flex items-center gap-2 font-normal">
                  <Plus size={18} />
                  Tilføj Persona
                </div>
              </Button>
            </div>

            {personas.length > 0 ? (
              <div className="flex flex-col gap-2">
                <Reorder.Group
                  axis="y"
                  values={personas}
                  onReorder={onReorderPersona}
                  className="flex w-full flex-col gap-y-4"
                  aria-label="Selected contacts"
                >
                  {personas.map((persona, index) => {
                    const key = `${persona.levels.join("-")}-${persona.groups.join("-")}-${persona.titles.map((t) => t.title).join("-")}-${persona.requirePhone}`;
                    return (
                      <PersonaListItem
                        key={key}
                        persona={persona}
                        index={index}
                        onRemove={onRemovePersona}
                        onEdit={() => handleOpenModal(index)}
                      />
                    );
                  })}
                </Reorder.Group>
              </div>
            ) : (
              <div className="text-theme-on-glass-variant flex items-center justify-center rounded-lg p-5 text-sm">
                Ingen personas tilføjet endnu
              </div>
            )}
          </div>
        </div>
      ) : (
        <EmptyState onPress={handleOpenModal} />
      )}

      <AddPersonaModal
        isOpen={isModalOpen}
        onOpenChange={handleCloseModal}
        onSave={handleSave}
        configuration={configuration}
        initialData={editingPersona}
        personas={personas}
      />
    </div>
  );
}
